import React, { useState, useEffect } from 'react';
import { Dropdown } from 'antd';
import { UserOutlined, LogoutOutlined, CreditCardOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { billingService } from '../services/billingService';
import i18n from '../i18n';

import UserAvatar from './UserAvatar';
import MyProfile from './MyProfile';

const UserMenu = ({ userName, notesStats }) => {
  const { logout, user } = useAuthStore();
  const { t } = useTranslation(['auth', 'messages']);
  const navigate = useNavigate();
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);

  useEffect(() => {
    // 确保默认语言是英文
    if (i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }

    const fetchSubscription = async () => {
      try {
        const status = await billingService.getSubscriptionStatus();
        setSubscriptionStatus(status);
      } catch (error) {
        console.error('Failed to fetch subscription status:', error);
      } finally {
        setIsLoadingSubscription(false);
      }
    };
    fetchSubscription();
  }, []);

  console.log('UserMenu - userName prop:', userName);
  console.log('UserMenu - user from store:', user);

  // Determine plan type based on subscription status
  const getPlanType = () => {
    if (isLoadingSubscription) {
      return null; // Don't show anything while loading
    }
    if (subscriptionStatus?.hasSubscription && subscriptionStatus?.status === 'active') {
      return 'Professional';
    }
    return 'Starter';
  };

  const userMenuItems = [
    {
      key: 'user-info',
      icon: <UserAvatar
        user={user}
        size="small"
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          height: '16px',
          width: '16px',
          lineHeight: '16px'
        }}
        autoGenerate={true}
      />,
      label: userName || user?.username || user?.email || t('auth:user'),
      disabled: true,
      style: {
        cursor: 'default',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e8e8e8'
      }
    },
    {
      type: 'divider'
    },
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('auth:menu.profile'),
    },
    {
      key: 'guide',
      icon: <QuestionCircleOutlined />,
      label: t('auth:menu.guide'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('auth:menu.logout'),
    },
  ];

  const handleUserMenuClick = async (e) => {
    if (e.key === 'logout') {
      logout();
    } else if (e.key === 'profile') {
      setProfileModalVisible(true);
    } else if (e.key === 'guide') {
      navigate('/guide');
    }
  };

  return (
    <>
      <Dropdown
        menu={{
          items: userMenuItems,
          onClick: handleUserMenuClick,
        }}
        trigger={['click']}
        placement="bottomRight"
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer',
          padding: '12px',
          borderRadius: '0px',
          backgroundColor: '#ffffff',
          border: 'none',
          transition: 'all 0.2s ease',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
        }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.08)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.04)';
          }}
        >
          {/* 左侧头像 */}
          <UserAvatar
            user={user}
            size="large"
            autoGenerate={true}
          />

          {/* 右侧用户信息 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {/* 第一行：用户名 */}
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#262626',
              lineHeight: '20px'
            }}>
              {userName || user?.username || user?.email || t('auth:user')}
            </div>

            {/* 第二行：Plan type - clickable */}
            {!isLoadingSubscription && (
              <div
                style={{
                  fontSize: '12px',
                  color: getPlanType() === 'Professional' ? '#1890ff' : '#8c8c8c',
                  lineHeight: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  transition: 'color 0.2s ease'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/membership');
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#1890ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = getPlanType() === 'Professional' ? '#1890ff' : '#8c8c8c';
                }}
              >
                <span>{getPlanType()}</span>
                {getPlanType() !== 'Professional' && (
                  <span
                    style={{
                      fontSize: '11px',
                      marginLeft: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px',
                      color: '#ff4d4f',
                      fontWeight: '500',
                      padding: '2px 5px',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff',
                      border: '1px solid #ffccc7'
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/membership');
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#ff7875';
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.borderColor = '#ffa39e';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#ff4d4f';
                      e.currentTarget.style.backgroundColor = '#ffffff';
                      e.currentTarget.style.borderColor = '#ffccc7';
                    }}
                  >
                    <CreditCardOutlined style={{ fontSize: '11px' }} />
                    <span>Subs</span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </Dropdown>

      <MyProfile
        visible={profileModalVisible}
        onClose={() => setProfileModalVisible(false)}
        notesStats={notesStats}
      />
    </>
  );
};

export default UserMenu; 